<template>
  <layout :title="pageTitle">
    <div class="columns">
      <div class="column is-3">
        <ul class="inner-menu box">
          <li>
            <ScrollLink to="#overview">Overview</ScrollLink>
          </li>
          <li>
            <ScrollLink to="#scores">Scores</ScrollLink>
          </li>
          <li>
            <ScrollLink to="#clients">Clients</ScrollLink>
          </li>
          <li>
            <ScrollLink to="#successDrivers">Success Drivers</ScrollLink>
          </li>
          <li>
            <ScrollLink to="#actions">Actions</ScrollLink>
          </li>
        </ul>
      </div>

      <div class="column is-9">
        <b-message v-if="!questionSet.isEditable" type="is-warning">
          This question set is locked
        </b-message>

        <div class="box">
          <a class="anchor" id="overview"></a>
          <h3>Overview</h3>
          <hr />
          <div class="columns">
            <div class="column">
              <b-field label="Name">
                <b-input
                  @input="updateQuestionSet"
                  v-model="questionSet.name"
                  :disabled="!questionSet.isEditable"
                ></b-input>
              </b-field>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column">
            <div class="box">
              <a class="anchor" name="clients"></a>
              <h3>Clients</h3>
              <hr />
              <p class="mb-2"><b>Linked Clients</b></p>
              <p v-if="questionSet.clients.length == 0">
                No clients have been linked
              </p>
              <div
                class="client mb-2"
                :key="client.id"
                v-for="client in questionSet.clients"
                style="display: flex; justify-content: space-between"
              >
                {{ client.name }}
                <div
                  class="icon-container"
                  style="cursor: pointer"
                  @click="detachClient(client)"
                >
                  <b-icon
                    pack="fas"
                    icon="trash"
                    type="is-danger"
                    size="is-small"
                  ></b-icon>
                </div>
              </div>
              <hr />
              <b-field v-if="clients.length > 0">
                <b-select
                  placeholder="Link a client"
                  v-model="selectedClient"
                  expanded
                  @input="addAttachedClient()"
                >
                  <option
                    :key="client.id"
                    v-for="client in clients"
                    :value="client"
                  >
                    {{ client.name }}
                  </option>
                </b-select>
              </b-field>
            </div>
          </div>
        </div>

        <div class="box">
          <a class="anchor" id="scores"></a>
          <h3>Scores</h3>
          <hr />
          <div class="block">
            <p><b>Slider Preview</b></p>
            <b-field>
              <b-slider
                :min="1"
                :max="questionSet.question_descriptions.length"
                :tooltip="false"
                size="is-medium"
              >
                <template
                  v-for="(score, index) in questionSet.question_descriptions"
                >
                  <b-slider-tick :value="index + 1" :key="index + 1">{{
                    index + 1
                  }}</b-slider-tick>
                </template>
              </b-slider>
            </b-field>
          </div>
          <div class="block">
            <p><b>Radio Preview</b></p>
            <b-field>
              <div class="question-radio">
                <draggable
                  v-model="questionSet.question_descriptions"
                  group="people"
                  @start="drag = true"
                  @end="drag = false"
                >
                  <div
                    v-for="(score, index) in questionSet.question_descriptions"
                    :key="index"
                  >
                    <div v-if="edit" class="is-flex edit-score-container">
                      <p class="mb-3 mr-2 ml-1 has-text-weight-semibold">
                        {{ index + 1 }}
                      </p>
                      <b-input
                        :value="score.name"
                        v-model="score.name"
                      ></b-input>
                      <div class="icon-container" @click="removeScore(index)">
                        <b-icon
                          pack="fas"
                          icon="trash"
                          type="is-danger"
                          size="is-small"
                        ></b-icon>
                      </div>
                    </div>
                    <b-radio v-else name="slider-radio" native-value="default">
                      {{ index + 1 }} - {{ score.name }}
                    </b-radio>
                  </div>
                </draggable>
              </div>
            </b-field>
            <span v-if="questionSet.isEditable">
              <b-button
                :type="edit ? 'is-primary' : 'is-text'"
                @click="edit = !edit"
              >
                <span v-if="edit" @click="updateScores()">Save Scores</span>
                <span v-else>Edit Scores</span>
              </b-button>
              <b-button
                type="is-primary"
                outlined
                v-if="edit"
                @click="addScore()"
                >Add Score</b-button
              >
            </span>
          </div>
        </div>

        <div class="mb-5"></div>
        <hr />

        <div class="block">
          <a class="anchor" id="successDrivers"></a>
          <div class="title-bar">
            <h3>Success Drivers</h3>
            <inertia-link
              v-if="questionSet.isEditable"
              :href="
                route(
                  'question-sets.setup.successDrivers',
                  this.questionSet.unique_id
                )
              "
            >
              <b-button type="is-primary ml-auto">Edit</b-button>
            </inertia-link>
          </div>
          <hr />
        </div>

        <draggable
          v-model="successDriversForm"
          group="people"
          @start="drag = true"
          @end="drag = false"
          @change="reorderSuccessDrivers"
        >
          <b-collapse
            class="card box"
            animation="slide"
            v-for="successDriver in successDriversForm"
            :key="successDriver.unique_id"
            :open="false"
          >
            <div
              slot="trigger"
              class="card-header"
              role="button"
              aria-controls="contentIdForA11y3"
            >
              <div class="card-header-title">
                {{ successDriver.order }} -
                <b-input
                  v-on:click.stop
                  class="ml-5"
                  v-model="successDriver.name"
                  expanded
                  :disabled="!questionSet.isEditable"
                  @blur="updateSuccessDriver(successDriver)"
                  style="min-width: 50%"
                ></b-input>
                <div
                  class="icon-container"
                  v-if="questionSet.isEditable"
                  @click="deleteSuccessDriver(successDriver)"
                >
                  <b-icon
                    pack="fas"
                    icon="trash"
                    type="is-danger"
                    size="is-small"
                    class="ml-auto"
                  ></b-icon>
                </div>
              </div>
            </div>
            <div class="card-content">
              <div class="content">
                <table>
                  <tr>
                    <th></th>
                    <th>Name</th>
                    <th>Type</th>
                    <th align="right">Shown for self feedback</th>
                    <th align="right">Shown for Internal</th>
                    <th align="right">Shown for External</th>
                    <th v-if="questionSet.isEditable">Action</th>
                  </tr>
                  <draggable
                    v-model="successDriver.questions"
                    tag="tbody"
                    @start="drag = true"
                    @end="drag = false"
                    @change="reorderQuestions(successDriver)"
                  >
                    <tr
                      v-for="(question, index) in successDriver.questions"
                      :key="question.id"
                    >
                      <td>
                        <b> {{ question.order }} - </b>
                      </td>
                      <td scope="row">
                        <b-input
                          :disabled="!questionSet.isEditable"
                          v-model="question.name"
                          @blur="updateQuestion(successDriver, question)"
                        ></b-input>
                      </td>
                      <td>
                        <b-select
                          :disabled="!questionSet.isEditable"
                          v-model="question.type"
                          expanded
                          @input="updateQuestion(successDriver, question)"
                        >
                          <option value="slider" selected>Slider</option>
                          <option value="text">Text</option>
                          <option value="radio">Radio</option>
                        </b-select>
                      </td>
                      <td align="right">
                        <b-checkbox
                          v-model="question.isShownForSelfFeedback"
                          :disabled="!questionSet.isEditable"
                          @input="updateQuestion(successDriver, question)"
                          :true-value="1"
                          :false-value="0"
                        >
                        </b-checkbox>
                      </td>
                      <td align="right">
                        <b-checkbox
                          v-model="question.isShownForInternal"
                          :disabled="!questionSet.isEditable"
                          @input="updateQuestion(successDriver, question)"
                          :true-value="1"
                          :false-value="0"
                        >
                        </b-checkbox>
                      </td>
                      <td align="right">
                        <b-checkbox
                          v-model="question.isShownForExternal"
                          :disabled="!questionSet.isEditable"
                          @input="updateQuestion(successDriver, question)"
                          :true-value="1"
                          :false-value="0"
                        >
                        </b-checkbox>
                      </td>
                      <td v-if="questionSet.isEditable">
                        <div
                          class="icon-container"
                          @click="
                            deleteQuestion(successDriver, index, question)
                          "
                        >
                          <b-icon
                            pack="fas"
                            icon="trash"
                            type="is-danger"
                            size="is-small"
                          ></b-icon>
                        </div>
                      </td>
                    </tr>
                  </draggable>
                </table>

                <b-button
                  v-if="questionSet.isEditable"
                  type="is-primary"
                  outlined
                  @click="addQuestion(successDriver)"
                  :disabled="isLoadingQuestion"
                  >Add Question</b-button
                >
              </div>
            </div>
          </b-collapse>
        </draggable>

        <div class="mb-5"></div>

        <div class="box">
          <a class="anchor" id="actions"></a>
          <h3>Actions</h3>
          <hr style="margin-top: 15px" />
          <b-button
            v-if="questionSet.isEditable"
            @click="showDeleteModal = true"
            class="is-danger"
          >
            Delete Question Set
          </b-button>
          <inertia-link
            :href="route('duplicate.questionSet', questionSet.unique_id)"
          >
            <b-button class="is-info"> Clone </b-button>
          </inertia-link>
          <a
            :href="
              route('question-set.exportQuestionSetCSV', questionSet.unique_id)
            "
            target="_BLANK"
          >
            <b-button class="is-info"> Export </b-button>
          </a>
          <b-button
            v-if="!questionSet.isEditable"
            @click="questionSet.isEditable = true"
            class="is-warning"
          >
            Unlock
          </b-button>
        </div>
      </div>
    </div>

    <!-- modals -->
    <b-modal
      v-model="showDeleteModal"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      aria-role="dialog"
      aria-modal
    >
      <DeleteModal
        :delete-route="
          route('question-sets.destroy', questionSet.unique_id).toString()
        "
        :name="questionSet.name"
        :message="'You are about to delete a question. This will also delete all success drivers, questions and answers under this question set.'"
      />
    </b-modal>
  </layout>
</template>

<script>
import Layout from './Layout';
import ScrollLink from '@/Components/Shared/ScrollLink';
// https://github.com/SortableJS/Vue.Draggable
import Draggable from 'vuedraggable';
import DeleteModal from '../../Components/Management/DeleteModal';

export default {
  components: {
    Layout,
    ScrollLink,
    Draggable,
    DeleteModal,
  },
  props: {
    questionSet: { type: Object },
    clients: { type: Array },
  },
  data() {
    return {
      pageTitle: 'Edit Question Set: ' + this.questionSet.name,
      edit: false,
      selectedClient: null,
      successDriversForm: this.questionSet.success_drivers,
      isLoadingQuestion: false,
      showDeleteModal: false,
    };
  },
  methods: {
    updateQuestionSet: function () {
      const data = {
        questionSet: this.questionSet,
      };
      if (this.questionSet.name !== null) {
        this.$inertia.patch(
          route('question-sets.update', this.questionSet.unique_id),
          data,
          {
            preserveScroll: true,
          }
        );
      }
    },
    addAttachedClient: function () {
      const data = {
        client: this.selectedClient,
      };
      let self = this;
      this.$inertia.patch(
        route('question-set.attachClient', this.questionSet.unique_id),
        data,
        {
          preserveScroll: true,
          onFinish: function () {
            self.selectedClient = null;
          },
        }
      );
    },
    detachClient: function (client) {
      const data = {
        client: client,
      };
      this.$inertia.patch(
        route('question-set.detachClient', this.questionSet.unique_id),
        data,
        {
          preserveScroll: true,
        }
      );
    },
    addScore: function () {
      this.questionSet.question_descriptions.push({
        id: this.questionSet.question_descriptions.length + 1,
        name: '',
        order: this.questionSet.question_descriptions.length + 1,
      });
    },
    removeScore: function (index) {
      this.questionSet.question_descriptions.splice(index, 1);
    },
    updateScores: function () {
      const data = {
        questionDescriptions: this.questionSet.question_descriptions,
      };
      this.$inertia.patch(
        route('questionDescriptions.update', this.questionSet.unique_id),
        data,
        {
          preserveScroll: true,
        }
      );
    },
    addSuccessDriver: function () {
      let successDriver = {
        name: '',
        order: this.questionSet.success_drivers.length + 1,
      };
      this.successDriversForm.push(successDriver);
    },
    updateSuccessDriver: function (successDriver) {
      var self = this;
      if (successDriver.unique_id) {
        this.$inertia.patch(
          route('success-drivers.update', successDriver.unique_id),
          {
            successDriver,
          },
          {
            preserveScroll: true,
            onFinish: function () {
              self.successDriversForm = self.questionSet.success_drivers;
            },
          }
        );
      } else {
        this.$inertia.post(
          route('success-drivers.store'),
          {
            questionSet: this.questionSet,
            successDriver,
          },
          {
            preserveScroll: true,
            onFinish: () => {
              self.successDriversForm = self.questionSet.success_drivers;
            },
          }
        );
      }
    },
    reorderSuccessDrivers: function () {
      var self = this;
      const data = {
        successDrivers: this.successDriversForm,
      };
      this.$inertia.put(route('successDrivers.reorder'), data, {
        preserveScroll: true,
        onFinish: function () {
          self.successDriversForm = self.questionSet.success_drivers;
        },
      });
    },
    deleteSuccessDriver: function (successDriver) {
      var self = this;
      this.$inertia.delete(
        route('success-drivers.destroy', successDriver.unique_id),
        {
          preserveScroll: true,
          onFinish: function () {
            self.successDriversForm = self.questionSet.success_drivers;
          },
        }
      );
    },
    addQuestion: function (successDriver) {
      let question = {
        name: '',
        type: 'slider',
        order: successDriver.questions.length + 1,
        isShownForSelfFeedback: 1,
      };

      successDriver.questions.push(question);

      this.isLoadingQuestion = true;
    },
    updateQuestion: function (successDriver, question) {
      var self = this;
      if (question.unique_id) {
        this.isLoadingQuestion = true;
        this.$inertia.patch(
          route('questions.update', question.unique_id),
          {
            question,
          },
          {
            preserveScroll: true,
            onFinish: () => {
              self.successDriversForm = self.questionSet.success_drivers;
              self.isLoadingQuestion = false;
            },
          }
        );
      } else if (question.name !== '') {
        this.$inertia.post(
          route('questions.store'),
          {
            successDriver,
            question,
          },
          {
            preserveScroll: true,
            onFinish: () => {
              self.successDriversForm = self.questionSet.success_drivers;
              self.isLoadingQuestion = false;
            },
          }
        );
      }
    },
    reorderQuestions: function (successDriver) {
      var self = this;
      const data = {
        questions: successDriver.questions,
      };
      this.$inertia.put(route('questions.reorder'), data, {
        preserveScroll: true,
        onFinish: function () {
          self.successDriversForm = self.questionSet.success_drivers;
        },
      });
    },
    deleteQuestion: function (successDriver, questionIndex, question) {
      var self = this;
      if (question.unique_id) {
        this.$inertia.delete(route('questions.destroy', question.unique_id), {
          preserveScroll: true,
          onFinish: () => {
            self.successDriversForm = self.questionSet.success_drivers;
            self.isLoadingQuestion = false;
          },
        });
      } else {
        successDriver.questions.splice(questionIndex, 1);
        this.isLoadingQuestion = false;
      }
    },
  },
};
</script>
